import { useState, createContext, useContext } from "react";

// Current year form translations (always same path - no year changes needed)
import langJSONStudentCurrent from "../data/current/bi-lang-student.json";
import langJSONTeacherCurrent from "../data/current/bi-lang-teacher.json";
import langJSONSchoolCurrent from "../data/current/bi-lang-school.json";

// Archive: 2025 form translations (for My Applications preview)
import langJSONStudent2025 from "../data/archive/2025/bi-lang-student-2025.json";
import langJSONTeacher2025 from "../data/archive/2025/bi-lang-teacher-2025.json";
import langJSONSchool2025 from "../data/archive/2025/bi-lang-school-2025.json";

// Common translations (shared across all years)
import langJSONCommon from "../data/bi-lang-common.json";

var dictionaryList: { [key: string]: { [key: string]: string } } = {
  en: {},
  sk: {},
  hi: {},
};

type TlangItem = {
  key: string;
  value: {
    en: string;
    sk: string;
    hi: string;
  };
};

export const languageOptions = { en: "English", sk: "Sanskrit", hi: "Hindi" };
const mergeDictionaries = (langJSON: TlangItem[]) => {
  langJSON.forEach((element: TlangItem) => {
    dictionaryList.en[element.key] = element.value.en;
    dictionaryList.sk[element.key] = element.value.sk;
    dictionaryList.hi[element.key] = element.value.hi;
  });
};

// Merge all translations (current year + archive + common)
[
  langJSONStudentCurrent,
  langJSONTeacherCurrent,
  langJSONSchoolCurrent,
  langJSONStudent2025,
  langJSONTeacher2025,
  langJSONSchool2025,
  langJSONCommon,
].forEach((langJSON) => {
  if (Array.isArray(langJSON)) {
    mergeDictionaries(langJSON);
  }
});

export const LanguageContext = createContext({
  userLanguage: "hi",
  dictionary: dictionaryList.hi,
  userLanguageChange: (selected: keyof typeof languageOptions) => {},
} as {
  userLanguage: string;
  dictionary: Record<string, string>;
  userLanguageChange: (selected: keyof typeof languageOptions) => void;
});

export function LanguageProvider({ children }: any) {
  const defaultLanguage = localStorage.getItem("rcml-lang") || "hi";

  const [userLanguage, setUserLanguage] = useState<string>(
    defaultLanguage as any,
  );

  const userLanguageChange = (selected: keyof typeof languageOptions) => {
    const newLanguage = languageOptions[selected] ? selected : "";
    setUserLanguage(newLanguage);
  };

  const provider = {
    userLanguage,
    dictionary: dictionaryList[userLanguage as keyof typeof dictionaryList],
    userLanguageChange: userLanguageChange,
  };
  return (
    <LanguageContext.Provider value={provider}>
      {children}
    </LanguageContext.Provider>
  );
}

function Text({ tid, def }: { tid: string; def?: string }): string {
  const languageContext = useContext(LanguageContext);

  // Check if languageContext or languageContext.dictionary is undefined
  if (!languageContext || !languageContext.dictionary) {
    return def || "";
  }

  // Ensure tid is a valid key of the dictionary
  const text =
    languageContext.dictionary[tid as keyof typeof languageContext.dictionary];

  // Return text if found, otherwise return default text
  return text !== undefined && text.length ? text : def || ""; // Wrap the text in a JSX element
}

export default Text;
